// src/pages/Employees.tsx — Employee register with WHS credential tracking
import { useState } from 'react'
import { useAuthStore } from '../stores/auth.store'
import { MOCK_EMPLOYEES, getCredentialHealth, PAY_RATES,
  calcApprenticeYear, calcIsAdultApprentice, EMPLOYMENT_TYPE_META,
} from '../utils/mockDataExtended'
import {
  getLeaveBalance, getPendingLeaveRequests, submitLeaveRequest,
  approveLeaveRequest, rejectLeaveRequest, getTAFEBlocks, addTAFEBlock,
  updateTAFEBlockStatus, formatLeaveBalance, LEAVE_TYPE_META, LeaveType,
} from '../utils/mockDataLeave'
import { formatDate, statusMeta } from '../utils/formatters'
import { useQuery } from '@tanstack/react-query'
import { fetchEmployees, updateEmployee, isNeonEmployee } from '../services/employeesService'
import {
  AlertTriangle, CheckCircle, Shield, Clock, Phone,
  Users, Plus, Search, ArrowLeft, X, ChevronRight,
  Mail, Briefcase, MapPin, Award, Activity, Download,
} from 'lucide-react'

type Employee = typeof MOCK_EMPLOYEES[0]

const TRADE_LABELS: Record<string, string> = {
  ELECTRICAL: 'Electrical', TRAFFIC_CONTROL: 'Traffic Control',
  CIVIL: 'Civil', ADMINISTRATION: 'Administration', ITS: 'ITS / Comms',
}
const EMP_TYPE_LABELS: Record<string, { label: string; bg: string; text: string }> = {
  FULL_TIME:    { label: 'Full-time',    bg: 'bg-green-100',  text: 'text-green-700' },
  CASUAL:       { label: 'Casual',       bg: 'bg-blue-100',   text: 'text-blue-700' },
  PART_TIME:    { label: 'Part-time',    bg: 'bg-purple-100', text: 'text-purple-700' },
  SUBCONTRACTOR:{ label: 'Subcontractor',bg: 'bg-amber-100',  text: 'text-amber-700' },
}
const TIER_LABELS: Record<string, string> = {
  A_DEVELOPER: 'Admin', B_PM: 'Project Manager',
  C_PROCUREMENT: 'Procurement', D_FIELD: 'Field',
}

function CredHealthBadge({ health }: { health: string }) {
  if (health === 'EXPIRED')  return <span className="flex items-center gap-1 text-xs font-medium text-red-700 bg-red-100 px-2 py-0.5 rounded-full"><AlertTriangle className="w-3 h-3" />Credential expired</span>
  if (health === 'EXPIRING') return <span className="flex items-center gap-1 text-xs font-medium text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full"><Clock className="w-3 h-3" />Expiring soon</span>
  return <span className="flex items-center gap-1 text-xs font-medium text-green-700 bg-green-100 px-2 py-0.5 rounded-full"><CheckCircle className="w-3 h-3" />Credentials OK</span>
}

function CredRow({ label, value, expiry }: { label: string; value?: string | null; expiry?: string | null }) {
  const today = new Date()
  const exp   = expiry ? new Date(expiry) : null
  const expired  = exp && exp < today
  const expiring = exp && !expired && (exp.getTime() - today.getTime()) < 30 * 24 * 60 * 60 * 1000
  return (
    <div className={`flex items-center justify-between py-2.5 border-b last:border-0 ${expired ? 'bg-red-50 -mx-4 px-4' : expiring ? 'bg-amber-50 -mx-4 px-4' : ''}`}>
      <div className="text-sm text-gray-600">{label}</div>
      <div className="flex items-center gap-2">
        {value && <span className="text-xs font-mono text-gray-500">{value}</span>}
        {expiry && (
          <span className={`text-xs font-medium ${expired ? 'text-red-700' : expiring ? 'text-amber-700' : 'text-gray-500'}`}>
            {expired ? '⚠ Expired ' : expiring ? '⚠ Expires ' : 'Expires '}{formatDate(expiry)}
          </span>
        )}
        {!value && !expiry && <span className="text-xs text-gray-400">—</span>}
      </div>
    </div>
  )
}

// Add Employee Modal
function AddEmployeeModal({ onClose, onAdd }: { onClose: () => void; onAdd: (e: any) => void }) {
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', phone: '', trade: 'ELECTRICAL', employmentType: 'FULL_TIME', payClassification: 'ELECTRICAL_A_GRADE', systemTier: 'D_FIELD' })
  const set = (k: string, v: string) => setForm(f => ({ ...f, [k]: v }))
  const num = String(MOCK_EMPLOYEES.length + 1).padStart(3, '0')
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onAdd({ ...form, id: `e_new_${Date.now()}`, employeeNumber: `EMP-${num}`, fullName: `${form.firstName} ${form.lastName}`, dateOfBirth: '', address: '', emergencyContact: '', emergencyPhone: '', currentJobId: null, currentJobNumber: null, startDate: new Date().toISOString().slice(0,10), credentials: { electricalLicenceNumber: null, electricalLicenceExpiry: null, firstAidExpiry: null, whsInductionExpiry: null, tcaExpiry: null, workingAtHeights: false, confinedSpaceEntry: false, hvAuthority: null } })
    onClose()
  }
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="font-semibold text-gray-900">Add employee</h2>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div><label className="block text-xs font-medium text-gray-700 mb-1">First name</label><input className="input" value={form.firstName} onChange={e => set('firstName', e.target.value)} required /></div>
            <div><label className="block text-xs font-medium text-gray-700 mb-1">Last name</label><input className="input" value={form.lastName} onChange={e => set('lastName', e.target.value)} required /></div>
          </div>
          <div><label className="block text-xs font-medium text-gray-700 mb-1">Email</label><input type="email" className="input" value={form.email} onChange={e => set('email', e.target.value)} required /></div>
          <div><label className="block text-xs font-medium text-gray-700 mb-1">Phone</label><input className="input" value={form.phone} onChange={e => set('phone', e.target.value)} /></div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Trade / discipline</label>
              <select className="input" value={form.trade} onChange={e => set('trade', e.target.value)}>
                {Object.entries(TRADE_LABELS).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Employment type</label>
              <select className="input" value={form.employmentType} onChange={e => set('employmentType', e.target.value)}>
                {Object.entries(EMP_TYPE_LABELS).map(([k,v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Pay classification</label>
            <select className="input" value={form.payClassification} onChange={e => set('payClassification', e.target.value)}>
              {Object.keys(PAY_RATES).map(k => <option key={k} value={k}>{k.replace(/_/g,' ')}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">System access tier</label>
            <select className="input" value={form.systemTier} onChange={e => set('systemTier', e.target.value)}>
              {Object.entries(TIER_LABELS).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
            <button type="submit" className="btn-primary">Add employee</button>
          </div>
        </form>
      </div>
    </div>
  )
}

// Edit Employee Modal
function EditEmployeeModal({ emp, onClose, onSave }: { emp: Employee; onClose: () => void; onSave: (updated: Partial<Employee>) => void }) {
  const [form, setForm] = useState({
    firstName:         emp.firstName,
    lastName:          emp.lastName,
    email:             emp.email,
    phone:             emp.phone,
    position:          (emp as any).role ?? (emp as any).position ?? '',
    trade:             emp.trade,
    employmentType:    emp.employmentType,
    payClassification: emp.payClassification,
    systemTier:        (emp as any).systemTier ?? 'D_FIELD',
    address:           emp.address ?? '',
    emergencyContact:  emp.emergencyContact ?? '',
    emergencyPhone:    emp.emergencyPhone   ?? '',
  })
  const set = (k: string, v: string) => setForm(f => ({ ...f, [k]: v }))
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSave({ ...form, fullName: `${form.firstName} ${form.lastName}`, role: form.position } as any)
    onClose()
  }
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden my-4">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="font-semibold text-gray-900">Edit — {emp.fullName}</h2>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div><label className="block text-xs font-medium text-gray-700 mb-1">First name</label><input className="input" value={form.firstName} onChange={e => set('firstName', e.target.value)} required /></div>
            <div><label className="block text-xs font-medium text-gray-700 mb-1">Last name</label><input className="input" value={form.lastName} onChange={e => set('lastName', e.target.value)} required /></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="block text-xs font-medium text-gray-700 mb-1">Email</label><input type="email" className="input" value={form.email} onChange={e => set('email', e.target.value)} /></div>
            <div><label className="block text-xs font-medium text-gray-700 mb-1">Phone</label><input className="input" value={form.phone} onChange={e => set('phone', e.target.value)} /></div>
          </div>
          <div><label className="block text-xs font-medium text-gray-700 mb-1">Home address</label><input className="input" value={form.address} onChange={e => set('address', e.target.value)} placeholder="Street address" /></div>
          <div><label className="block text-xs font-medium text-gray-700 mb-1">Position / Job title</label><input className="input" value={form.position} onChange={e => set('position', e.target.value)} placeholder="e.g. WHS Officer, Warehouse Manager, Site Supervisor" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="block text-xs font-medium text-gray-700 mb-1">Emergency contact</label><input className="input" value={form.emergencyContact} onChange={e => set('emergencyContact', e.target.value)} /></div>
            <div><label className="block text-xs font-medium text-gray-700 mb-1">Emergency phone</label><input className="input" value={form.emergencyPhone} onChange={e => set('emergencyPhone', e.target.value)} /></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Trade</label>
              <select className="input" value={form.trade} onChange={e => set('trade', e.target.value)}>
                {Object.entries(TRADE_LABELS).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Employment type</label>
              <select className="input" value={form.employmentType} onChange={e => set('employmentType', e.target.value)}>
                {Object.entries(EMP_TYPE_LABELS).map(([k,v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Pay classification</label>
              <select className="input" value={form.payClassification} onChange={e => set('payClassification', e.target.value)}>
                {Object.keys(PAY_RATES).map(k => <option key={k} value={k}>{k.replace(/_/g,' ')}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">System tier</label>
              <select className="input" value={form.systemTier} onChange={e => set('systemTier', e.target.value)}>
                {Object.entries(TIER_LABELS).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
          </div>
          <p className="text-xs text-gray-400">Changes are saved locally this session. Neon persistence wired in Phase 6.</p>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
            <button type="submit" className="btn-primary">Save changes</button>
          </div>
        </form>
      </div>
    </div>
  )
}

// Employee detail
function EmployeeDetail({ emp, onBack, onEdit }: { emp: Employee; onBack: () => void; onEdit: (emp: Employee) => void }) {
  const [tab, setTab] = useState<'profile'|'credentials'|'assignment'|'apprenticeship'|'leave'>('profile')
  const health = getCredentialHealth(emp)
  const et = EMP_TYPE_LABELS[emp.employmentType]
  const payRate = PAY_RATES[emp.payClassification] ?? 0
  const isApprentice = emp.employmentType === 'APPRENTICE' || emp.employmentType === 'HOST_APPRENTICE'
  const leaveBal     = getLeaveBalance(emp.id)
  const tafBlocks    = isApprentice ? getTAFEBlocks(emp.id) : []
  const tabs = [
    { key: 'profile',         label: 'Profile' },
    { key: 'credentials',     label: 'Credentials' },
    { key: 'assignment',      label: 'Current assignment' },
    ...(isApprentice ? [{ key: 'apprenticeship', label: 'Apprenticeship' }] : []),
    { key: 'leave',           label: 'Leave & WC' },
  ] as const

  return (
    <div>
      <button onClick={onBack} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900 mb-4">
        <ArrowLeft className="w-4 h-4" /> All employees
      </button>

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden mb-5">
        <div className="px-6 py-5">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 rounded-full bg-brand-navy flex items-center justify-center text-white text-xl font-bold flex-shrink-0">
                {emp.firstName[0]}{emp.lastName[0]}
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">{emp.fullName}</h1>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <span className="text-sm text-gray-500">{emp.employeeNumber}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${et.bg} ${et.text}`}>{et.label}</span>
                  <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{TRADE_LABELS[emp.trade] ?? emp.trade}</span>
                  {((emp as any).role || (emp as any).position) && (
                    <span className="text-xs bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded-full">{(emp as any).role ?? (emp as any).position}</span>
                  )}
                  <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">{TIER_LABELS[emp.systemTier] ?? emp.systemTier}</span>
                  <CredHealthBadge health={health} />
                </div>
              </div>
            </div>
            <button onClick={() => onEdit(emp)} className="btn-secondary text-xs">Edit profile</button>
          </div>
        </div>
        <div className="grid grid-cols-3 divide-x border-t text-sm">
          <div className="px-5 py-3"><div className="text-xs text-gray-500">Pay classification</div><div className="font-medium mt-0.5">{emp.payClassification.replace(/_/g,' ')}</div></div>
          <div className="px-5 py-3"><div className="text-xs text-gray-500">Base rate</div><div className="font-medium mt-0.5">${payRate.toFixed(2)}/hr</div></div>
          <div className="px-5 py-3"><div className="text-xs text-gray-500">Start date</div><div className="font-medium mt-0.5">{formatDate(emp.startDate)}</div></div>
        </div>
      </div>

      <div className="flex gap-1 mb-5 bg-gray-100 rounded-xl p-1">
        {tabs.map(t => (
          <button key={t.key} onClick={() => setTab(t.key as any)}
            className={`flex-1 text-xs font-medium py-2 px-3 rounded-lg transition-colors ${tab === t.key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-5">
        {tab === 'profile' && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm"><Mail className="w-4 h-4 text-gray-400" /><span>{emp.email}</span></div>
                <div className="flex items-center gap-2 text-sm"><Phone className="w-4 h-4 text-gray-400" /><span>{emp.phone}</span></div>
                {emp.address && <div className="flex items-start gap-2 text-sm"><MapPin className="w-4 h-4 text-gray-400 mt-0.5" /><span>{emp.address}</span></div>}
              </div>
              <div className="space-y-3">
                <div className="text-sm"><span className="text-gray-500">Emergency contact:</span> <span className="font-medium">{emp.emergencyContact}</span></div>
                <div className="text-sm"><span className="text-gray-500">Emergency phone:</span> <span className="font-medium">{emp.emergencyPhone}</span></div>
                {emp.dateOfBirth && <div className="text-sm"><span className="text-gray-500">DOB:</span> <span className="font-medium">{formatDate(emp.dateOfBirth)}</span></div>}
              </div>
            </div>
          </div>
        )}

        {tab === 'credentials' && (
          <div>
            <p className="text-xs text-gray-500 mb-4">Credentials highlighted in amber expire within 30 days. Red credentials are expired. WHSO can update expiry dates.</p>
            <CredRow label="Electrical licence number" value={emp.credentials.electricalLicenceNumber} expiry={emp.credentials.electricalLicenceExpiry} />
            <CredRow label="First aid certificate" expiry={emp.credentials.firstAidExpiry} />
            <CredRow label="WHS site induction" expiry={emp.credentials.whsInductionExpiry} />
            <CredRow label="Traffic control authority (TCA)" expiry={emp.credentials.tcaExpiry} />
            {emp.credentials.hvAuthority && <CredRow label="HV authority" value={emp.credentials.hvAuthority} />}
            <div className="mt-4 pt-4 border-t">
              <p className="text-xs font-medium text-gray-700 mb-3">Additional authorisations</p>
              <div className="flex flex-wrap gap-2">
                {[
                  ['Working at heights', emp.credentials.workingAtHeights],
                  ['Confined space entry', emp.credentials.confinedSpaceEntry],
                  ['HV authority holder', !!emp.credentials.hvAuthority],
                ].map(([label, has]) => (
                  <span key={label as string} className={`text-xs px-2.5 py-1 rounded-full font-medium ${has ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400 line-through'}`}>
                    {has ? '✓ ' : ''}{label as string}
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}

        {tab === 'apprenticeship' && isApprentice && (
          <div className="space-y-5">
            {/* Apprentice identity */}
            <div className="grid grid-cols-2 gap-4">
              {[
                { label: 'Commencement date', value: (emp as any).apprenticeCommencementDate ? formatDate((emp as any).apprenticeCommencementDate) : '—' },
                { label: 'Current year', value: (emp as any).apprenticeCommencementDate ? `Year ${calcApprenticeYear((emp as any).apprenticeCommencementDate)} of 4` : '—' },
                { label: 'Adult apprentice (21+ at start)', value: (emp as any).isAdultApprentice ? 'Yes — NMW floor applies' : `No — year-based rate only` },
                { label: 'Target licence class', value: (emp as any).targetLicenceClass ?? '—' },
                { label: 'Training provider', value: (emp as any).trainingProvider ?? '—' },
                { label: 'Contract reference', value: (emp as any).trainingContractRef ?? '—' },
                { label: 'Contract term', value: (emp as any).trainingContractTerm ? `${(emp as any).trainingContractTerm} years` : '—' },
                { label: 'Expected completion', value: (emp as any).trainingContractExpiry ? formatDate((emp as any).trainingContractExpiry) : '—' },
              ].map(f => (
                <div key={f.label}>
                  <div className="text-xs text-gray-500">{f.label}</div>
                  <div className="font-medium text-sm text-gray-900 mt-0.5">{f.value}</div>
                </div>
              ))}
            </div>

            {/* GTO arrangement */}
            {(emp as any).gtoArrangement && (
              <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs font-bold text-orange-700 bg-orange-100 px-2 py-0.5 rounded-full">GTO Hosted</span>
                  <span className="text-sm font-semibold text-gray-900">{(emp as any).gtoName}</span>
                </div>
                <p className="text-xs text-orange-700">This apprentice is employed by the Group Training Organisation. MCB CE pays the host rate of <strong>${(emp as any).gtoHostRate}/day</strong> — payroll is managed by the GTO, not through MCB CE STP.</p>
              </div>
            )}

            {/* TAFE blocks */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-semibold text-gray-900">TAFE attendance schedule</h4>
                <span className="text-xs text-gray-400">{tafBlocks.length} block{tafBlocks.length !== 1 ? 's' : ''} on record</span>
              </div>
              {tafBlocks.length === 0 ? (
                <div className="bg-yellow-50 border border-yellow-200 rounded-xl px-4 py-3 text-xs text-yellow-800">
                  No TAFE blocks scheduled. Add block release dates so the roster grid can block allocation on TAFE days.
                </div>
              ) : (
                <div className="space-y-2">
                  {tafBlocks.map(b => (
                    <div key={b.id} className={`border rounded-xl p-4 ${b.status === 'COMPLETED' ? 'bg-green-50 border-green-200' : b.status === 'DEFERRED' ? 'bg-gray-50 border-gray-200' : 'bg-yellow-50 border-yellow-300'}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-semibold text-sm text-gray-900">Block {b.blockNumber} — {b.year}</span>
                            <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${b.status === 'COMPLETED' ? 'bg-green-100 text-green-700' : b.status === 'DEFERRED' ? 'bg-gray-100 text-gray-500' : 'bg-yellow-100 text-yellow-700'}`}>{b.status}</span>
                            <span className="text-xs text-gray-400">{b.releaseType === 'BLOCK' ? 'Block release' : 'Day release'}</span>
                          </div>
                          <div className="text-xs text-gray-600">{formatDate(b.startDate)} — {formatDate(b.endDate)} • {b.totalDays} days • {b.totalHours}h</div>
                          <div className="text-xs text-gray-500 mt-0.5">{b.institution}</div>
                          {b.units.length > 0 && <div className="text-xs text-gray-400 mt-0.5">Units: {b.units.join(', ')}</div>}
                        </div>
                      </div>
                      {b.notes && <div className="text-xs text-gray-400 mt-2 italic">{b.notes}</div>}
                    </div>
                  ))}
                </div>
              )}
              <p className="text-xs text-gray-400 mt-2">
                TAFE dates automatically block roster allocation cells and generate TAFE_ATTENDANCE timesheet entries at ordinary rate.
              </p>
            </div>
          </div>
        )}

        {tab === 'leave' && (
          <div className="space-y-5">
            {/* Workers comp status */}
            {(emp as any).workerCompActive && (
              <div className="bg-amber-50 border-2 border-amber-400 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="w-4 h-4 text-amber-600" />
                  <span className="font-bold text-amber-800">Workers’ compensation — ACTIVE</span>
                </div>
                <div className="grid grid-cols-2 gap-3 text-xs text-amber-800">
                  <div><span className="text-amber-600">Claim reference:</span><div className="font-medium">{(emp as any).workerCompClaimRef ?? '—'}</div></div>
                  <div><span className="text-amber-600">Injury date:</span><div className="font-medium">{(emp as any).workerCompInjuryDate ? formatDate((emp as any).workerCompInjuryDate) : '—'}</div></div>
                  <div><span className="text-amber-600">Insurer:</span><div className="font-medium">{(emp as any).workerCompInsurer ?? '—'}</div></div>
                </div>
                <p className="text-xs text-amber-700 mt-2">This employee is blocked from roster allocation while a workers’ comp claim is active. Timesheet entries must be coded as WORKERS_COMP for accurate payroll treatment.</p>
              </div>
            )}
            {!(emp as any).workerCompActive && (
              <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-3 flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                <span className="text-sm text-green-800">No active workers’ compensation claim</span>
              </div>
            )}

            {/* Leave balances */}
            <div>
              <h4 className="text-sm font-semibold text-gray-900 mb-3">Leave balances</h4>
              {!leaveBal ? (
                <div className="text-sm text-gray-400">No leave balance record found for this employee.</div>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  {([
                    { key:'annualLeave',      label:'Annual leave',         color:'text-blue-700',   bg:'bg-blue-50',   border:'border-blue-200' },
                    { key:'personalLeave',    label:'Personal / sick leave', color:'text-red-700',    bg:'bg-red-50',    border:'border-red-200'  },
                    { key:'toil',             label:'TOIL balance',          color:'text-teal-700',   bg:'bg-teal-50',   border:'border-teal-200' },
                    { key:'longServiceLeave', label:'Long service leave',    color:'text-purple-700', bg:'bg-purple-50', border:'border-purple-200'},
                  ] as const).map(f => (
                    <div key={f.key} className={`${f.bg} border ${f.border} rounded-xl p-4`}>
                      <div className={`text-lg font-bold ${f.color}`}>{formatLeaveBalance((leaveBal as any)[f.key])}</div>
                      <div className="text-xs text-gray-500 mt-0.5">{f.label}</div>
                    </div>
                  ))}
                </div>
              )}
              {leaveBal && (
                <p className="text-xs text-gray-400 mt-2">
                  Last accrual: {formatDate(leaveBal.lastAccrualDate)}.
                  Balances update when timesheets are approved. Contact PM to adjust a balance.
                </p>
              )}
            </div>
          </div>
        )}

        {tab === 'assignment' && (
          <div>
            {emp.currentJobId ? (
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center gap-2 mb-1">
                  <Briefcase className="w-4 h-4 text-blue-600" />
                  <span className="font-semibold text-blue-900">{emp.currentJobNumber}</span>
                </div>
                <p className="text-sm text-blue-700">Currently allocated to this job</p>
                <div className="mt-3 text-xs text-blue-600">View full resource matrix → Resource Matrix module</div>
              </div>
            ) : (
              <div className="text-center py-10 text-gray-400">
                <Activity className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                <p className="text-sm">Not currently allocated to a project</p>
                <p className="text-xs mt-1">Allocate via the Resource Matrix module</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

// Employee list


// ─────────────────────────────────────────────────────────────────
// PHASE 1 — EMPLOYEE CREDENTIAL EXPIRY REPORT
// Exports a CSV showing all employee credentials with expiry status.
// Legal obligation — MCB must track and act on expired credentials.
// Also generates a printable HTML report for WHSO records.
// ─────────────────────────────────────────────────────────────────
function exportCredentialReport(employees: any[], format: 'csv' | 'print' = 'csv') {
  const now   = new Date()
  const today = now.toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })
  const fmtDate = (d: string | null) => d ? new Date(d).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'
  const daysLeft = (d: string | null) => d ? Math.ceil((new Date(d).getTime() - now.getTime()) / 86400000) : null

  const CRED_TYPES = [
    { key: 'electricalLicenceExpiry',  label: 'Electrical licence',       numKey: 'electricalLicenceNumber' },
    { key: 'firstAidExpiry',           label: 'First aid certificate',     numKey: null },
    { key: 'whsInductionExpiry',       label: 'WHS site induction',        numKey: null },
    { key: 'tcaExpiry',                label: 'Traffic control authority', numKey: null },
  ]

  const getStatus = (days: number | null) =>
    days === null ? 'Not recorded' : days < 0 ? 'EXPIRED' : days <= 30 ? 'Expiring 30d' : days <= 90 ? 'Expiring 90d' : 'Current'

  if (format === 'csv') {
    const rows: string[][] = [
      ['Employee Name','Employee Number','Classification','Credential Type','Credential Number','Expiry Date','Days Remaining','Status']
    ]
    for (const emp of employees) {
      for (const ct of CRED_TYPES) {
        const expiry = emp.credentials?.[ct.key] ?? null
        const num    = ct.numKey ? (emp.credentials?.[ct.numKey] ?? '') : ''
        const days   = daysLeft(expiry)
        if (!expiry) continue
        rows.push([
          emp.fullName, emp.employeeNumber ?? '',
          (emp.payClassification ?? '').replace(/_/g,' '),
          ct.label, num, fmtDate(expiry),
          days !== null ? String(days) : '—',
          getStatus(days),
        ])
      }
    }
    const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g,'""')}"`).join(',')).join(String.fromCharCode(10))
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' })
    const url  = URL.createObjectURL(blob)
    const a    = document.createElement('a')
    a.href = url
    a.download = `MCB_Credential_Expiry_${new Date().toISOString().slice(0,10)}.csv`
    document.body.appendChild(a); a.click()
    document.body.removeChild(a); URL.revokeObjectURL(url)
  } else {
    // Print-ready HTML report
    const expired   = [] as string[]
    const expiring30 = [] as string[]
    const expiring90 = [] as string[]
    employees.forEach(emp => CRED_TYPES.forEach(ct => {
      const days = daysLeft(emp.credentials?.[ct.key] ?? null)
      if (days === null) return
      const entry = `${emp.fullName} — ${ct.label}${emp.credentials?.[ct.numKey ?? ''] ? ' ('+emp.credentials[ct.numKey!]+')' : ''}`
      if (days < 0)       expired.push(entry + ` · EXPIRED ${fmtDate(emp.credentials[ct.key])}`)
      else if (days <= 30) expiring30.push(entry + ` · ${days}d (${fmtDate(emp.credentials[ct.key])})`)
      else if (days <= 90) expiring90.push(entry + ` · ${days}d (${fmtDate(emp.credentials[ct.key])})`)
    }))

    const empTable = employees.map((emp, i) => `
      <tr style="background:${i%2===0?'#fff':'#f9fafb'}">
        <td style="padding:5px 10px;font-weight:bold">${emp.fullName}</td>
        <td style="padding:5px 10px;font-family:monospace">${emp.employeeNumber ?? '—'}</td>
        <td style="padding:5px 10px">${(emp.payClassification ?? '').replace(/_/g,' ')}</td>
        ${CRED_TYPES.map(ct => {
          const days = daysLeft(emp.credentials?.[ct.key] ?? null)
          const bg = days === null ? '#f9fafb' : days < 0 ? '#fee2e2' : days <= 30 ? '#fef3c7' : days <= 90 ? '#fefce8' : '#f0fdf4'
          const txt = days === null ? '<span style="color:#9ca3af">—</span>' : days < 0 ? `<span style="color:#b91c1c;font-weight:bold">EXPIRED<br><small>${fmtDate(emp.credentials[ct.key])}</small></span>` : `<small>${fmtDate(emp.credentials[ct.key])}<br>(${days}d)</small>`
          return `<td style="padding:5px 8px;text-align:center;font-size:9pt;background:${bg}">${txt}</td>`
        }).join('')}
      </tr>`).join('')

    const alertSection = (title: string, items: string[], bg: string, color: string) =>
      items.length === 0 ? '' : `<h2 style="color:${color};border-bottom:1.5px solid ${color};padding-bottom:3px;margin:14px 0 8px">${title} (${items.length})</h2>
      <ul style="margin:0 0 10px;padding-left:20px">${items.map(x => `<li style="margin:3px 0;font-size:10pt">${x}</li>`).join('')}</ul>`

    const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Employee Credential Report</title>
<style>*{box-sizing:border-box}body{font-family:Arial,sans-serif;font-size:10pt;color:#111;margin:14mm}
h1{font-size:20pt;font-weight:900;color:#1B3A6B;margin:0}
.hdr{display:flex;justify-content:space-between;align-items:flex-end;border-bottom:3px solid #1B3A6B;padding-bottom:10px;margin-bottom:12px}
table{width:100%;border-collapse:collapse;font-size:9pt}th{background:#1B3A6B;color:#fff;padding:5px 10px;text-align:left}
.footer{font-size:7.5pt;color:#aaa;text-align:center;margin-top:16px;border-top:1px solid #eee;padding-top:6px}
@media print{body{margin:10mm}}</style></head><body>
<div class="hdr"><div><h1>MCB Civil Electrical</h1><div style="font-size:9pt;color:#888">ABN 12 345 678 901</div></div>
<div style="text-align:right"><div style="font-size:17pt;font-weight:900;color:#1B3A6B">EMPLOYEE CREDENTIAL EXPIRY REPORT</div>
<div style="font-size:9pt;color:#888">As at: ${today}</div></div></div>
${alertSection('⚠ Expired — immediate action required', expired, '#fee2e2', '#b91c1c')}
${alertSection('⚠ Expiring within 30 days', expiring30, '#fef3c7', '#b45309')}
${alertSection('Expiring within 90 days', expiring90, '#fefce8', '#92400e')}
<h2 style="color:#1B3A6B;border-bottom:1.5px solid #1B3A6B;padding-bottom:3px;margin:14px 0 8px">All employee credentials</h2>
<table><thead><tr><th>Employee</th><th>Number</th><th>Classification</th><th>Electrical licence</th><th>First aid</th><th>WHS induction</th><th>TCA</th></tr></thead>
<tbody>${empTable}</tbody></table>
<div class="footer">Confidential — MCB Civil Electrical HR records · Generated ${today}</div>
</body></html>`
    const win = window.open('', '_blank', 'width=1100,height=750')
    if (!win) return
    win.document.write(html); win.document.close()
    setTimeout(() => { win.focus(); win.print() }, 350)
  }
}


function downloadEmployeeTemplate() {
  const rows = [
    ['Full Name','Email','Phone','Trade','Employment Type','Pay Classification','Electrical Licence Number','Electrical Licence Expiry','First Aid Expiry','WHS Induction Expiry'],
    ['Mark Stevens','mark.stevens@company.com.au','0412 000 111','ELECTRICAL','FULL_TIME','ELECTRICAL_A_GRADE','99887QLD','2027-05-01','2026-09-01','2027-02-01'],
    ['Priya Singh','priya.singh@company.com.au','0413 222 333','CIVIL','CASUAL','CIVIL_LABOURER','','','2026-08-15','2027-01-20'],
  ]
  const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g,'""')}"`).join(',')).join(String.fromCharCode(10))
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' })
  const url  = URL.createObjectURL(blob)
  const a    = document.createElement('a')
  a.href = url; a.download = 'MCB_Employee_Register_Import_Template.csv'
  document.body.appendChild(a); a.click()
  document.body.removeChild(a); URL.revokeObjectURL(url)
}


function parseEmpCSVLine(line: string): string[] {
  const out: string[] = []
  let cur = '', inQuotes = false
  for (let i = 0; i < line.length; i++) {
    const ch = line[i]
    if (ch === '"') {
      if (inQuotes && line[i+1] === '"') { cur += '"'; i++ }
      else inQuotes = !inQuotes
    } else if (ch === ',' && !inQuotes) {
      out.push(cur); cur = ''
    } else cur += ch
  }
  out.push(cur)
  return out.map(s => s.trim())
}


function parseEmployeeCSV(text: string, existingCount: number): { employees: any[]; errors: string[] } {
  const rows = text.split(/\r?\n/).filter(r => r.trim().length > 0)
  if (rows.length < 2) return { employees: [], errors: ['File is empty or has no data rows'] }
  const header = parseEmpCSVLine(rows[0]).map(h => h.toLowerCase())
  const idx = {
    name: header.indexOf('full name'), email: header.indexOf('email'), phone: header.indexOf('phone'),
    trade: header.indexOf('trade'), empType: header.indexOf('employment type'), payClass: header.indexOf('pay classification'),
    elNum: header.indexOf('electrical licence number'), elExp: header.indexOf('electrical licence expiry'),
    faExp: header.indexOf('first aid expiry'), whsExp: header.indexOf('whs induction expiry'),
  }
  const errors: string[] = []
  const employees: any[] = []
  for (let i = 1; i < rows.length; i++) {
    const cells = parseEmpCSVLine(rows[i])
    const fullName = cells[idx.name]?.trim()
    if (!fullName) { errors.push(`Row ${i+1}: missing full name — skipped`); continue }
    const nameParts = fullName.split(' ')
    const num = String(existingCount + employees.length + 1).padStart(3, '0')
    employees.push({
      id: `e_imported_${Date.now()}_${i}`,
      employeeNumber: `EMP-${num}`,
      firstName: nameParts[0] ?? fullName, lastName: nameParts.slice(1).join(' ') || '—',
      fullName, email: cells[idx.email]?.trim() || '—', phone: cells[idx.phone]?.trim() || '—',
      dateOfBirth: null, address: '—', emergencyContact: '—', emergencyPhone: '—',
      trade: cells[idx.trade]?.trim().toUpperCase() || 'CIVIL',
      employmentType: cells[idx.empType]?.trim().toUpperCase() || 'FULL_TIME',
      payClassification: cells[idx.payClass]?.trim().toUpperCase() || 'CIVIL_LABOURER',
      systemTier: 'D_FIELD', currentJobId: null, currentJobNumber: null,
      startDate: new Date().toISOString().slice(0,10),
      credentials: {
        electricalLicenceNumber: cells[idx.elNum]?.trim() || null,
        electricalLicenceExpiry: cells[idx.elExp]?.trim() || null,
        firstAidExpiry: cells[idx.faExp]?.trim() || null,
        whsInductionExpiry: cells[idx.whsExp]?.trim() || null,
        tcaExpiry: null, workingAtHeights: false, confinedSpaceEntry: false, hvAuthority: null,
      },
    })
  }
  return { employees, errors }
}


function EmployeeImportButton({ existingCount, onImportMany }: { existingCount: number; onImportMany: (rows: any[]) => void }) {
  const [importing, setImporting] = useState(false)
  const [result, setResult] = useState<{ count: number; errors: string[] } | null>(null)

  const handleFile = (file: File) => {
    setImporting(true)
    const reader = new FileReader()
    reader.onload = e => {
      const text = String(e.target?.result ?? '')
      const { employees, errors } = parseEmployeeCSV(text, existingCount)
      if (employees.length > 0) onImportMany(employees)
      setResult({ count: employees.length, errors })
      setImporting(false)
    }
    reader.onerror = () => { setResult({ count: 0, errors: ['Failed to read file'] }); setImporting(false) }
    reader.readAsText(file)
  }

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <button onClick={downloadEmployeeTemplate} className="flex items-center gap-1.5 text-xs px-3 py-1.5 border border-gray-200 hover:bg-gray-50 text-gray-700 rounded-lg font-medium">
        <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
        Download template
      </button>
      <label className="flex items-center gap-1.5 text-xs px-3 py-1.5 border border-indigo-200 hover:bg-indigo-50 text-indigo-700 rounded-lg font-medium cursor-pointer">
        <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" /></svg>
        {importing ? 'Importing…' : 'Bulk import CSV'}
        <input type="file" accept=".csv" className="hidden" disabled={importing}
          onChange={e => { const f = e.target.files?.[0]; if (f) handleFile(f); e.target.value = '' }} />
      </label>
      {result && (
        <span className={`text-xs font-medium ${result.errors.length > 0 ? 'text-amber-600' : 'text-green-600'}`}>
          {result.count > 0 ? `✓ ${result.count} employee${result.count!==1?'s':''} imported` : ''}
          {result.errors.length > 0 ? ` · ${result.errors.length} skipped` : ''}
        </span>
      )}
    </div>
  )
}
export function Employees() {
  const { isTier } = useAuthStore()
  const [localAdded, setLocalAdded]   = useState<Employee[]>([])
  const [localEdits, setLocalEdits]   = useState<Record<string, Partial<Employee>>>({})
  const [selected, setSelected]       = useState<Employee | null>(null)
  const [editing, setEditing]         = useState<Employee | null>(null)
  const [search, setSearch]           = useState('')
  const [tradeFilter, setTradeFilter] = useState('ALL')
  const [typeFilter, setTypeFilter]   = useState('ALL')
  const [showAdd, setShowAdd]         = useState(false)

  // Live employees from Neon via Cloudflare Worker API
  const { data: apiEmployees = [], isLoading: empLoading, isError: empError } = useQuery({
    queryKey: ['employees'],
    queryFn:  fetchEmployees,
    staleTime: 60_000,
  })

  // Merge: real API employees first, then mock fallback for any not yet in DB, then locally added
  const apiNumbers = new Set(apiEmployees.map((e: any) => e.employeeNumber))
  const mockFallback = MOCK_EMPLOYEES.filter(e => !apiNumbers.has(e.employeeNumber))
  const employees: Employee[] = [
    ...(apiEmployees as unknown as Employee[]),
    ...mockFallback,
    ...localAdded,
  ].map(e => localEdits[e.id] ? { ...e, ...localEdits[e.id] } as Employee : e)

  const handleSaveEdit = (updated: Partial<Employee>) => {
    if (!editing) return
    // Apply to local overlay immediately (optimistic update)
    setLocalEdits(prev => ({ ...prev, [editing.id]: updated }))
    if (selected?.id === editing.id) setSelected(prev => prev ? { ...prev, ...updated } as Employee : prev)
    // Persist to Neon if this is a real employee (UUID ID), fire-and-forget
    if (isNeonEmployee(editing.id)) {
      updateEmployee(editing.id, updated as any).catch(err =>
        console.error('Failed to persist employee edit to Neon:', err)
      )
    }
    setEditing(null)
  }

  if (selected) return (
    <>
      <EmployeeDetail
        emp={{ ...selected, ...(localEdits[selected.id] ?? {}) } as Employee}
        onBack={() => setSelected(null)}
        onEdit={e => setEditing(e)}
      />
      {editing && <EditEmployeeModal emp={editing} onClose={() => setEditing(null)} onSave={handleSaveEdit} />}
    </>
  )

  const filtered = employees.filter(e => {
    const matchS = !search || e.fullName.toLowerCase().includes(search.toLowerCase()) || e.email.toLowerCase().includes(search.toLowerCase()) || e.employeeNumber.toLowerCase().includes(search.toLowerCase())
    const matchT = tradeFilter === 'ALL' || e.trade === tradeFilter
    const matchE = typeFilter  === 'ALL' || e.employmentType === typeFilter
    return matchS && matchT && matchE
  })

  const expiringCount = employees.filter(e => getCredentialHealth(e) !== 'OK').length

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Employees</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {employees.length} staff
            {expiringCount > 0 && <span className="ml-2 text-amber-600 font-medium">· {expiringCount} credential alert{expiringCount !== 1 ? 's' : ''}</span>}
          {apiEmployees.length > 0 && <span className="ml-2 text-blue-600 font-medium">· {apiEmployees.length} live from Neon</span>}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <EmployeeImportButton existingCount={employees.length} onImportMany={rows => setLocalAdded(prev => [...prev, ...(rows as Employee[])])} />
          <button onClick={() => exportCredentialReport(employees, 'csv')} className="btn-secondary flex items-center gap-2 text-sm">
            <Download className="w-4 h-4" /> Credential CSV
          </button>
          {isTier('B_PM') && (
            <button onClick={() => setShowAdd(true)} className="btn-primary flex items-center gap-2"><Plus className="w-4 h-4" />Add employee</button>
          )}
        </div>
      </div>

      {empLoading && (
        <div className="flex items-center gap-2 text-sm text-blue-700 bg-blue-50 border border-blue-100 rounded-xl px-4 py-3">
          <div className="w-3.5 h-3.5 border-2 border-blue-400 border-t-transparent rounded-full animate-spin flex-shrink-0" />
          Loading live employees from Neon...
        </div>
      )}
      {empError && (
        <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
          ⚠ API unreachable — showing mock data only. Check VITE_API_URL in your .env.
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-48">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input className="input pl-9" placeholder="Name, email, employee number…" value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select className="input text-sm w-auto" value={tradeFilter} onChange={e => setTradeFilter(e.target.value)}>
          <option value="ALL">All trades</option>
          {Object.entries(TRADE_LABELS).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select className="input text-sm w-auto" value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
          <option value="ALL">All types</option>
          {Object.entries(EMP_TYPE_LABELS).map(([k,v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="divide-y divide-gray-100">
          {filtered.length === 0 ? (
            <div className="py-14 text-center text-gray-400"><Users className="w-8 h-8 mx-auto mb-2 text-gray-300" /><p className="text-sm">No employees match</p></div>
          ) : filtered.map(emp => {
            const health = getCredentialHealth(emp)
            const et = EMP_TYPE_LABELS[emp.employmentType]
            return (
              <div key={emp.id} onClick={() => setSelected(emp)}
                className="flex items-center gap-4 px-5 py-3.5 hover:bg-gray-50 cursor-pointer group transition-colors">
                <div className="w-9 h-9 rounded-full bg-brand-navy flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
                  {emp.firstName[0]}{emp.lastName[0]}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-0.5">
                    <span className="font-semibold text-gray-900">{emp.fullName}</span>
                    <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${et.bg} ${et.text}`}>{et.label}</span>
                    {emp.currentJobNumber && <span className="text-xs bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded-full">{emp.currentJobNumber}</span>}
                  </div>
                  <p className="text-xs text-gray-500">{emp.employeeNumber} · {TRADE_LABELS[emp.trade] ?? emp.trade} · {emp.payClassification.replace(/_/g,' ')}</p>
                </div>
                <div className="hidden sm:block flex-shrink-0"><CredHealthBadge health={health} /></div>
                <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-gray-500 flex-shrink-0" />
              </div>
            )
          })}
        </div>
        <div className="px-5 py-3 border-t bg-gray-50 text-xs text-gray-500">
          {filtered.length} employees shown
        </div>
      </div>

      {showAdd && <AddEmployeeModal onClose={() => setShowAdd(false)} onAdd={e => { setLocalAdded(prev => [...prev, e]); setShowAdd(false) }} />}
    </div>
  )
}
